"use client";

/**
 * Whether an analysis, a calculation or an analyst run is in flight,
 * for the pill rows that sit above those pages: switching pages in the
 * middle of a run abandoned it (owner, 2026-10-11: "make the pills
 * disabled while the calculator or the analyzer is loading").
 */
import { useEffect, useSyncExternalStore } from "react";

let busy = false;
const subs = new Set<() => void>();

export function setAnalysisBusy(v: boolean) {
  if (busy === v) return;
  busy = v;
  subs.forEach((f) => f());
}

export function useAnalysisBusy(): boolean {
  return useSyncExternalStore(
    (cb) => { subs.add(cb); return () => { subs.delete(cb); }; },
    () => busy,
    () => false,
  );
}

/** Reports a page's loading flag while the page is mounted. */
export function useReportAnalysisBusy(loading: boolean) {
  useEffect(() => {
    setAnalysisBusy(loading);
    return () => setAnalysisBusy(false);
  }, [loading]);
}

/* A result on the analyze page: nothing sits above the answer then (owner
 * decision 1, 2026-10-29), so the pill row over it steps aside until the
 * page goes back to its empty form. */
let resultShown = false;
const resultSubs = new Set<() => void>();

function setResultShown(v: boolean) {
  if (resultShown === v) return;
  resultShown = v;
  resultSubs.forEach((f) => f());
}

export function useResultShown(): boolean {
  return useSyncExternalStore(
    (cb) => { resultSubs.add(cb); return () => { resultSubs.delete(cb); }; },
    () => resultShown,
    () => false,
  );
}

/** Reports whether the page shows a result while the page is mounted. */
export function useReportResultShown(shown: boolean) {
  useEffect(() => {
    setResultShown(shown);
    return () => setResultShown(false);
  }, [shown]);
}
